import React, { useState, useEffect } from 'react';
import './CustomModal.css';

interface CustomModalProps {
  show: boolean;
  handleClose: () => void;
  handleConsult: (portId: string, type: number, portName: string, month: string, year: string) => void;
  title: string;
  portName: string;
  historyData: { status: string; duration: string; start: string; end: string }[];
  portId: string;
  type: number;
}

const CustomModal: React.FC<CustomModalProps> = ({
  show,
  handleClose,
  handleConsult,
  title,
  portName,
  historyData,
  portId,
  type
}) => {
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [timeZone, setTimeZone] = useState<'local' | 'utc'>('local');

  function formatDateTime(dateTimeString: string, type: 'local' | 'utc', language: string = 'en-US'): string {
    const date = new Date(dateTimeString);
  
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      year: 'numeric',  // Cambiado a 'numeric' en lugar de 'string'
      month: 'numeric', // Cambiado a 'numeric'
      day: 'numeric',   // Cambiado a 'numeric'
      hour12: true
    };
  
    if (type === "utc") {
      // Formato UTC con AM/PM usando el idioma especificado
      const utcTime = date.toLocaleString(language, { ...options, timeZone: 'UTC' });
      return `${utcTime} (UTC)`;
    } else if (type === "local") {
      // Formato local con AM/PM usando el idioma especificado
      const localTime = date.toLocaleString(language, options);
  
      // Calcular el desplazamiento con respecto a UTC en minutos
      const offsetMinutes = date.getTimezoneOffset();
      const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
      const offsetSign = offsetMinutes > 0 ? '-' : '+';
      const offsetString = `GMT${offsetSign}${offsetHours}`;
  
      return `${localTime} (${offsetString})`;
    } else {
      throw new Error("Invalid type. Please use 'local' or 'utc'.");
    }
  }

  useEffect(() => {
    if (show) {
      setMonth('');
      setYear('');
    }
  }, [show]);

  if (!show) {
    return null;
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConsult(portId, type, portName, month, year);
  };

  const handleReload = () => {
    setMonth('');
    setYear('');
    handleConsult(portId, type, portName, '', '');
  };

 // Función para alternar entre 'local' y 'utc'
 const toggleTimeZone = () => {
  setTimeZone(prev => (prev === 'local' ? 'utc' : 'local'));
};
  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal-connectivity">
        <div className="custom-modal-header">
          <h4>{title}</h4>
          <div className="custom-modal-subtitle">
            <p>{portName}</p>
          </div>
          <button onClick={handleClose} className="close-button">&times;</button>
        </div>
        <div className="custom-modal-body">
          <form onSubmit={handleFormSubmit}>
            <div className="form-row-inline">
              <div className="form-group">
                <label htmlFor="month">Month:</label>
                <select
                  id="month"
                  className="form-select border-marino f-parrafo fs-p text-dk text-center custom-select"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  <option value="" disabled selected hidden>(e.g., Jan..)</option>
                  <option value="01">January</option>
                  <option value="02">February</option>
                  <option value="03">March</option>
                  <option value="04">April</option>
                  <option value="05">May</option>
                  <option value="06">June</option>
                  <option value="07">July</option>
                  <option value="08">August</option>
                  <option value="09">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="year">Year:</label>
                <input
                  type="number"
                  id="year"
                  className="form-control"
                  placeholder="(e.g., 2024)"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>
              <div className="form-group-buttons">
                <button type="submit" className="btn-light-marino f-parrafo">Search</button>
                <button type="button" className="btn-light-marino f-parrafo" onClick={handleReload}>
                  &#x21bb;
                </button>
              </div>
            </div>
          </form>

          {/* Tabla del historial */}
          <div className="history-table">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {historyData.length > 0 ? (
                  historyData.map((event, index) => (
                    <tr key={index}>
                      <td>{event.status}</td>
                      <td>{formatDateTime(event.start, timeZone, navigator.language || 'en-US' )}</td>
                      <td>{formatDateTime(event.end, timeZone, navigator.language || 'en-US' )}</td>
                      <td>{event.duration}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
  {/* Time zone toggle */}
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <button
      onClick={toggleTimeZone}
      style={{
        display: 'flex',
        alignItems: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '5px',
        transition: 'background-color 0.3s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      title={`Switch to ${timeZone === 'local' ? 'UTC' : 'Local'} Time`}
    >
      <i
        className={`fas fa-clock ${timeZone === 'local' ? 'fa-globe' : 'fa-map-marker-alt'}`}
        style={{ marginRight: '10px', fontSize: '20px' }}
      />
      <span>{timeZone === 'local' ? 'Local Time' : 'UTC Time'}</span>
    </button>
  </div>

  {/* Close button */}
  <div className="custom-modal-footer">
    <button
      onClick={handleClose}
      className="btn-cancel btn border border-pink text-pink f-parrafo"
      style={{ padding: '8px 16px' }}
    >
      Close
    </button>
  </div>
</div>

      </div>
    </div>
  );
};

export default CustomModal;
